import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {CartStore} from '../../../../core/stores/cart.store';
import {skip, takeUntil} from 'rxjs/operators';
import {OrderStore} from '../../../../core/stores/order.store';
import {Subject, zip} from 'rxjs';
import {OrderStatus, OrderStatusUser} from '../../../../core/models/order.model';
import {ActivatedRoute, Router} from '@angular/router';
import {Customer} from '../../../../core/models/customer.model';
import {PaymentService} from '../../../../core/services/payment.service';
import {RouteService} from '../../../../core/services/route.service';
import {PaymentStore, StatePayment} from '../../../../core/stores/payment.store';
import {environment} from '../../../../../environments/environment';
import {RestaurantStore} from '../../../../core/stores/restaurant.store';
import {ItemValidateService} from '../../../../core/services/item-validate.service';
import {GroupOrderTab, OrderTab} from '../../../../core/models/payment-item.model';
import {OrderApiService} from '../../../../core/services/order.api.service';
import {SharedOrderService} from '../../../../core/services/shared-order.service';

@Component({
  selector: 'app-order-complete',
  templateUrl: './order-complete.component.html',
  styleUrls: ['./order-complete.component.scss']
})
export class OrderCompleteComponent implements OnInit, OnDestroy {
  public readonly locationId: string;
  public readonly merchantId: string;
  public isScrollStartPosition = true;
  private destroyed$ = new Subject<void>();
  public pastOrdersStatus: OrderStatus[] = [];
  public customer: Customer;
  public isLoading = false;
  public estimatedTimeInMinutes: number;
  public estimatedTimeDelivery: number;
  public hideETA: boolean;
  public isHideShareButton = false;
  public isTab = false;
  public isGroupOrder = false;
  public tabLink = '';
  public newNavigator = (window.navigator as any);
  public orderCompletedStatus: OrderStatusUser[] = [];

  constructor(
    public restaurantStore: RestaurantStore,
    private cartStore: CartStore,
    private orderStore: OrderStore,
    private paymentStore: PaymentStore,
    private paymentService: PaymentService,
    private orderApiService: OrderApiService,
    private route: ActivatedRoute,
    private router: Router,
    private routeService: RouteService,
    private leadTimeService: ItemValidateService,
    public sharedOrderService: SharedOrderService
  ) {
    this.locationId = this.route.snapshot.params.locationId;
    this.merchantId = this.route.snapshot.params.merchantId;
  }

  public get orderTabTitle(): string {
    if (this.cartStore.tab) {
      return this.cartStore.tab.square_orders.length > 1 ? 'Your order has been added to your tab' : 'You’ve started a tab';
    } else {
      return this.cartStore.groupTab.rounds.length > 1 ? 'Your order has been added to your tab' : 'You’ve started a tab';
    }
  }

  public get isGroup(): boolean {
    return this.restaurantStore.isGroup || this.isGroupOrder;
  }

  ngOnInit() {
    this.isLoading = true;
    this.resetOrderData();
    if (this.newNavigator.share === undefined) {
      this.isHideShareButton = true;
    }
    this.isTab = !!this.cartStore.tab || !!this.cartStore.groupTab;
    if (this.isTab) {
      if (this.cartStore.tab) {
        this.tabLink = `${environment.tabUrl}tabs/${this.cartStore.tab.tab_id}`;
        this.orderApiService.getTabById(this.cartStore.tab.tab_id)
          .subscribe((tab: OrderTab) => {
            this.cartStore.tab = tab;
            this.isLoading = false;
          }, () => this.isLoading = false);
      } else {
        this.isGroupOrder = true;
        this.tabLink = `${environment.tabUrl}g/tabs/${this.cartStore.groupTab.group_order_id}`;
        this.orderApiService.getGroupTabById(this.cartStore.groupTab.group_order_id)
          .subscribe((tab: GroupOrderTab) => {
            this.cartStore.groupTab = tab;
            this.isLoading = false;
          }, () => this.isLoading = false);
      }
      this.isLoading = false;
    }
    this.orderStore.bootstrapAllPendingOrderStatus(this.locationId, true, this.isGroup);
    this.customer = this.paymentStore.customer;
    this.getUserOrders();
    this.getETA();
    this.listenOrderList();
  }

  @HostListener('unloaded')
  public ngOnDestroy(): void {
    if (!this.isTab) {
      this.paymentStore.customer = new Customer();
    }
    this.destroyed$.next();
  }

  public updateHeaderLogo(e): void {
    this.isScrollStartPosition = e.detail.currentY === 0;
  }

  public closeTab(): void {
    if (this.cartStore.tab) {
      const locationUrl = this.routeService.getLocationUrl();
      this.router.navigate([locationUrl, 'close-tab'])
        .catch(error => console.error(error));
    } else if (this.cartStore.groupTab) {
      const groupUrl = this.routeService.createGroupUrl(this.cartStore.groupTab.group_id);
      this.router.navigate([groupUrl, 'close-tab'])
        .catch(error => console.log(error));
    }
  }

  public getEstimatedTime(orderStatus: OrderStatus): number {
    return orderStatus.fulfillmentType === 'delivery' ? this.estimatedTimeDelivery : this.estimatedTimeInMinutes;
  }

  private resetOrderData(): void {
    for (const locationId of Object.keys(this.cartStore.orderMap)) {
      this.cartStore.deleteItemsFromCartByLocationId(locationId);
    }
    this.paymentStore.setStatePayment(StatePayment.INIT);
    this.cartStore.orderMap = {};
    this.cartStore.pickupAt = null;
    this.cartStore.isAsap = true;
    this.cartStore.loyalty = null;
    this.paymentStore.setStatePayment(StatePayment.INIT);
  }

  public async openShareOptions() {
    try {
      this.newNavigator.share({
        title: 'Tab ordering link',
        text: 'To add to my tab, visit',
        url: this.tabLink,
      })
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing', error));
    } catch (err) {
      console.error('Share failed:', err.message);
    }
  }

  public orderTabAgain(): void {
    if (!this.paymentStore.customer.givenName) {
      const customerId = this.cartStore.tab ? this.cartStore.tab.customer_id : this.cartStore.groupTab.customer_id;
      this.paymentService.getCustomerById(customerId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((customer) => {
          this.paymentStore.customer = customer;
          this.backToLocation();
        });
    } else {
      this.backToLocation();
    }
  }

  public backToLocation(): void {
    this.restaurantStore.backToStorePage();
  }

  private listenOrderList(): void {
    zip(this.orderStore.pendingOrdersStatus$.pipe(skip(1)),
        this.orderStore.pastOrdersStatus$.pipe(skip(1)))
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([pendingOrderStatusList, pastOrderStatusList]) => {
        this.filterOrderList(this.pastOrdersStatus, pastOrderStatusList);
        this.isLoading = false;
      });
  }

  private getETA(): void {
    this.estimatedTimeDelivery = this.restaurantStore.estimateDeliveryTime;
    this.restaurantStore.restaurantAvailableStatus$
      .subscribe(status => {
        if (status) {
          this.estimatedTimeInMinutes = this.leadTimeService.convertLeadTimeToMinutes(status.estimatedTimeToBeginPreparation);
          this.hideETA = status.hideETA;
        }
      });
  }

  private filterOrderList(orderList: OrderStatus[], fullOrderStatusList: OrderStatus[]) {
    for (const order of fullOrderStatusList) {
      if (order.customerId === this.customer.squareCustomerId) {
        orderList.push(order);
      }
    }
  }

  private getUserOrders(): void {
    if (this.restaurantStore.isGroup) {
      this.orderApiService.getGroupUserOrder(this.restaurantStore.group.groupId, this.customer.orderBotUserId).subscribe(orderList => {
        this.orderCompletedStatus = orderList;
        this.isLoading = false;
      }, () => {
        this.isLoading = false;
      });
      return;
    }
    this.orderApiService.getUserOrder(this.locationId, this.merchantId, this.customer.orderBotUserId).subscribe(orderList => {
      this.orderCompletedStatus = orderList;
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
    });
  }

}
